/**
 * features/journal/JournalPanel.tsx
 *
 * Orchestration root for the Journal & Playbook panel.
 *
 * NOTE — TradePlan / WaitRecord form responsibilities:
 * These are AUDIT-domain concerns, not journal concerns. They live in:
 *   - features/audit/components/TradePlansView.tsx  (TradePlanEditor)
 *   - features/audit/components/WaitLogView.tsx     (WaitRecordEditor, WaitLogQuickAdd)
 * The journal feature only handles JournalEntry (manual trade log) and
 * JournalDraft (AI-prefilled decision drafts). No TradePlanForm or
 * WaitRecordForm belongs in this feature.
 */
import React, { useState, useMemo } from 'react';
import { useStore } from '../../store';
import { JournalEntry } from '../../types';
import { JournalEditor }    from './components/JournalEditor';
import { JournalEntryList } from './components/JournalEntryList';
import { JournalDraftPanel } from './components/JournalDraftPanel';
import { JournalStatsView } from './components/JournalStatsView';
import { useJournalState, buildExportCSV } from './hooks/useJournalState';
import { Analytics71APanel } from '../analytics71a/Analytics71APanel';

const SETUP_TYPES = [
  'VWAP Reclaim Long', 'VWAP Rejection Short', 'OR Breakout Long', 'OR Failed Breakout Short',
  'Liquidity Sweep Reversal', 'Gamma Acceleration', 'Extension Fade', 'Trend Pullback', 'Max Pain Pin', 'Custom',
];
const REGIMES = ['Mean Reversion', 'Breakout', 'Trend Continuation', 'No Trade'];

const blank = (sym?: string): Omit<JournalEntry, 'id'> => ({
  date: new Date().toISOString().split('T')[0], symbol: sym || '', setup: '', regime: '', dir: '',
  entry: null, stop: null, target: null, exit: null, result: null, rMultiple: null,
  notes: '', tags: [], outcome: null,
});

export function JournalPanel({ activeSymbol }: { activeSymbol: string | null }) {
  const {
    journal, addJournalEntry, updateJournalEntry, deleteJournalEntry,
    journalDrafts, activeJournalDraftId, updateJournalDraft, deleteJournalDraft, setActiveJournalDraft,
  } = useStore();

  const [view, setView] = useState<'log' | 'stats' | 'drafts' | 'activity'>('log');

  React.useEffect(() => {
    if (activeJournalDraftId) setView('drafts');
  }, [activeJournalDraftId]);

  const [showForm,     setShowForm]     = useState(false);
  const [editId,       setEditId]       = useState<number | null>(null);
  const [form,         setForm]         = useState<Omit<JournalEntry, 'id'>>(blank(activeSymbol || undefined));
  const [filterSym,    setFilterSym]    = useState('');
  const [filterSetup,  setFilterSetup]  = useState('');
  const [filterRegime, setFilterRegime] = useState('');

  const startNew  = () => { setForm(blank(activeSymbol || undefined)); setEditId(null); setShowForm(true); };
  const startEdit = (e: JournalEntry) => { setForm({ ...e }); setEditId(e.id); setShowForm(true); };

  const pNum = (v: string | number | null | undefined) =>
    typeof v === 'number' ? v : parseFloat(String(v || ''));

  const save = () => {
    if (!form.symbol || !form.setup) return;
    const entry  = form.entry  !== null ? pNum(form.entry)  : null;
    const stop   = form.stop   !== null ? pNum(form.stop)   : null;
    const exit_  = form.exit   !== null ? pNum(form.exit)   : null;
    const result = form.result !== null ? pNum(form.result) : (entry && exit_ ? exit_ - entry : null);
    const rMultiple = result !== null && entry && stop ? result / Math.abs(entry - stop) : null;
    const outcome: JournalEntry['outcome'] =
      result === null ? 'open' : result > 0 ? 'win' : result < 0 ? 'loss' : 'breakeven';
    const parsed: Omit<JournalEntry, 'id'> = {
      ...form, entry: entry || null, stop: stop || null, exit: exit_ || null,
      result, rMultiple: rMultiple ? +rMultiple.toFixed(2) : null, outcome,
    };
    if (editId) updateJournalEntry({ ...parsed, id: editId });
    else        addJournalEntry(parsed);
    setShowForm(false); setEditId(null);
  };

  const filtered = useMemo(() => journal
    .filter(e =>
      (!filterSym    || e.symbol.toUpperCase().includes(filterSym.toUpperCase())) &&
      (!filterSetup  || e.setup  === filterSetup) &&
      (!filterRegime || e.regime === filterRegime))
    .sort((a, b) => b.id - a.id),
    [journal, filterSym, filterSetup, filterRegime]
  );

  const stats = useJournalState(journal);

  const exportCSV = () => {
    const csv  = buildExportCSV(stats.completed);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url;
    a.download = `MER_Journal_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="fsb mb12">
        <div className="sl" style={{ marginBottom: 0 }}>Journal & Playbook</div>
        <div className="flex gap6">
          <div className="tab-row" style={{ margin: 0, padding: 2 }}>
            {[['log', 'Log'], ['stats', 'Stats']].map(([id, l]) => (
              <button key={id} className={`tab ${view === id ? 'on' : ''}`}
                onClick={() => setView(id as any)} style={{ padding: '3px 10px', fontSize: 11 }}>{l}</button>
            ))}
            <button className={`tab ${view === 'drafts' ? 'on' : ''}`}
              onClick={() => setView('drafts')} style={{ padding: '3px 10px', fontSize: 11, position: 'relative' }}>
              Drafts
              {journalDrafts.length > 0 && (
                <span style={{ marginLeft: 4, background: 'var(--cyan)', color: '#000',
                  borderRadius: 8, fontSize: 8, padding: '0 4px', fontWeight: 700 }}>
                  {journalDrafts.length}
                </span>
              )}
            </button>
            <button className={`tab ${view === 'activity' ? 'on' : ''}`}
              onClick={() => setView('activity')} style={{ padding: '3px 10px', fontSize: 11 }}>
              Activity
            </button>
          </div>
          <button className="btn btn-sm" onClick={exportCSV}
            title="Export all completed trades to CSV" style={{ fontFamily: 'var(--mono)', fontSize: 10 }}>
            ⬇ CSV
          </button>
          <button className="btn btn-primary btn-sm" onClick={startNew}>+ Log Trade</button>
        </div>
      </div>

      {/* Entry form — JournalEditor handles new/edit JournalEntry records */}
      {showForm && (
        <JournalEditor
          form={form}
          editId={editId}
          onSave={save}
          onCancel={() => { setShowForm(false); setEditId(null); }}
          onChange={patch => setForm(f => ({ ...f, ...patch }))}
        />
      )}

      {/* Log view — filter bar + entry cards */}
      {view === 'log' && (
        <>
          <div className="flex gap6 mb8" style={{ flexWrap: 'wrap' }}>
            <input placeholder="Filter symbol…" value={filterSym} onChange={e => setFilterSym(e.target.value)}
              style={{ flex: '1 0 80px', background: 'var(--bg3)', border: '1px solid var(--b1)', borderRadius: 6,
                color: 'var(--t0)', padding: '4px 8px', fontFamily: 'var(--sans)', fontSize: 13, outline: 'none' }} />
            <select value={filterSetup} onChange={e => setFilterSetup(e.target.value)}
              style={{ flex: '2 0 120px', background: 'var(--bg3)', border: '1px solid var(--b1)', borderRadius: 6,
                color: 'var(--t0)', padding: '4px 8px', fontFamily: 'var(--sans)', fontSize: 13 }}>
              <option value="">All setups</option>
              {SETUP_TYPES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select value={filterRegime} onChange={e => setFilterRegime(e.target.value)}
              style={{ flex: '2 0 120px', background: 'var(--bg3)', border: '1px solid var(--b1)', borderRadius: 6,
                color: 'var(--t0)', padding: '4px 8px', fontFamily: 'var(--sans)', fontSize: 13 }}>
              <option value="">All regimes</option>
              {REGIMES.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <JournalEntryList entries={filtered} onEdit={startEdit} onDelete={deleteJournalEntry} />
        </>
      )}

      {/* Stats view — KPI grid + performance tables */}
      {view === 'stats' && <JournalStatsView stats={stats} />}

      {/* Drafts view — AI-prefilled decision drafts */}
      {view === 'drafts' && (
        <JournalDraftPanel
          drafts={journalDrafts}
          activeId={activeJournalDraftId}
          onSelect={setActiveJournalDraft}
          onUpdate={updateJournalDraft}
          onDelete={deleteJournalDraft}
        />
      )}

      {/* Activity view — Phase 7.1A analytics over logged entries */}
      {view === 'activity' && <Analytics71APanel />}
    </div>
  );
}
